import { useLingui } from '@lingui/react';
import { AlertCircle, Clock, Loader2, Mail, MailX } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { formatMoneyCents } from '@/lib/money';
import type { billing_DunningBlockerDTO } from '@/api/models/billing_DunningBlockerDTO';
import type { billing_DunningRegateDTO } from '@/api/models/billing_DunningRegateDTO';

type BlockerReason = 'no_recipient' | 'send_failed' | 'suppressed' | 'awaiting_approval';

interface Props {
  blocker: billing_DunningBlockerDTO;
  onRetry: () => void;
  onOpenEmailCockpit?: () => void;
  onCancel?: () => void;
  retryPending: boolean;
  cancelPending?: boolean;
}

const ICONS: Record<BlockerReason, React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>> = {
  no_recipient: Mail,
  send_failed: AlertCircle,
  suppressed: MailX,
  awaiting_approval: Clock,
};

export function DunningBlockerBanner({
  blocker,
  onRetry,
  onOpenEmailCockpit,
  onCancel,
  retryPending,
  cancelPending,
}: Props) {
  const { i18n } = useLingui();
  const reason = (blocker.reason ?? 'send_failed') as BlockerReason;
  const Icon = ICONS[reason] ?? AlertCircle;
  const sinceDate = blocker.since ? new Date(blocker.since) : new Date();
  const blockedFor = formatDistanceToNow(sinceDate, { addSuffix: false });

  // awaiting_approval is a benign "awaiting human action" state — render with a
  // yellow accent instead of the destructive red used by error blockers.
  const isApprovalGate = reason === 'awaiting_approval';
  // A re-gated letter (#1948): the drain put an approved letter back to
  // approval because the amounts changed. Same gate, but the reason leads.
  const regate = isApprovalGate ? blocker.regate : undefined;
  const containerClass = isApprovalGate
    ? 'rounded-lg border border-warning-on-surface/30 bg-warning-muted p-4 mb-4'
    : 'rounded-lg border border-destructive/50 bg-destructive/5 p-4 mb-4';
  const iconClass = isApprovalGate
    ? 'h-5 w-5 text-warning-on-surface mt-0.5 shrink-0'
    : 'h-5 w-5 text-destructive mt-0.5 shrink-0';
  const titleClass = isApprovalGate ? 'font-semibold text-warning-on-surface' : 'font-semibold text-destructive';

  const title = regate
    ? i18n._({ id: 'dunning.blocker.title.regated', message: 'Amounts changed since approval' })
    : reason === 'no_recipient'
      ? i18n._({ id: 'dunning.blocker.title.noRecipient' })
      : reason === 'send_failed'
        ? i18n._({ id: 'dunning.blocker.title.sendFailed' })
        : reason === 'awaiting_approval'
          ? i18n._({ id: 'dunning.blocker.title.awaitingApproval' })
          : i18n._({ id: 'dunning.blocker.title.suppressed' });

  const descriptionValues = { stage: blocker.stage ?? '', since: blockedFor };
  const description = regate
    ? i18n._({
        id: 'dunning.blocker.desc.regated',
        message: 'The letter was regenerated because the amounts changed. Preview the new letter and approve it again.',
      })
    : reason === 'no_recipient'
      ? i18n._({ id: 'dunning.blocker.desc.noRecipient', values: descriptionValues })
      : reason === 'send_failed'
        ? i18n._({ id: 'dunning.blocker.desc.sendFailed', values: descriptionValues })
        : reason === 'awaiting_approval'
          ? i18n._({
              id: 'dunning.blocker.desc.awaitingApprovalReview',
              message: 'This letter waits for your approval. Preview it, then approve and send it.',
            })
          : i18n._({ id: 'dunning.blocker.desc.suppressed', values: descriptionValues });

  const primaryLabel = retryPending
    ? i18n._({ id: 'common.sending', message: 'Sending...' })
    : isApprovalGate
      ? i18n._({ id: 'dunning.blocker.action.approveAndSend', message: 'Approve and send' })
      : i18n._({ id: 'dunning.blocker.action.sendNow' });

  return (
    <div role="alert" className={containerClass} data-testid={`dunning-blocker-banner-${reason}`}>
      <div className="flex items-start gap-3">
        <Icon className={iconClass} aria-hidden />
        <div className="flex-1 min-w-0">
          <h3 className={titleClass}>{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
          {regate?.edits_replaced && (
            <p className="text-sm text-muted-foreground mt-1">
              {i18n._({ id: 'dunning.blocker.desc.regatedEditsReplaced', message: 'Your earlier edits were replaced.' })}
            </p>
          )}
          {regate && <RegateChanges regate={regate} />}
          {/* The re-gate reason is already the translated heading; the raw
              English last_error would only repeat it. */}
          {blocker.last_error && !regate && (
            <p className="text-xs text-muted-foreground mt-1 font-mono break-all">
              {blocker.last_error}
            </p>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {reason !== 'suppressed' && (
              <Button
                size="sm"
                variant="default"
                onClick={onRetry}
                disabled={retryPending}
                aria-busy={retryPending}
                data-testid="dunning-blocker-send-now"
              >
                {retryPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                {primaryLabel}
              </Button>
            )}
            {/* Cancel only meaningful for awaiting_approval — for other blockers
                cancel is destructive and goes through the FSM via the drawer's
                Cancel button instead. */}
            {isApprovalGate && onCancel && (
              <Button
                size="sm"
                variant="outline"
                onClick={onCancel}
                disabled={cancelPending}
                data-testid="dunning-blocker-cancel"
              >
                {i18n._({ id: 'dunning.blocker.action.cancel' })}
              </Button>
            )}
            {blocker.email_log_id && onOpenEmailCockpit && (
              <Button size="sm" variant="outline" onClick={onOpenEmailCockpit}>
                {isApprovalGate
                  ? i18n._({ id: 'dunning.blocker.action.previewLetter', message: 'Preview letter' })
                  : i18n._({ id: 'dunning.blocker.action.openEmailCockpit' })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** What changed between the approved letter and the regenerated one. */
function RegateChanges({ regate }: { regate: billing_DunningRegateDTO }) {
  const { i18n } = useLingui();
  const currency = regate.currency;
  const difference = regate.outstanding_cents - regate.previous_outstanding_cents;
  const added = regate.added_invoice_numbers ?? [];
  const removed = regate.removed_invoice_numbers ?? [];

  return (
    <dl
      className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm"
      data-testid="dunning-regate-changes"
    >
      <dt className="text-muted-foreground">
        {i18n._({ id: 'dunning.blocker.regate.previousTotal', message: 'Approved total' })}
      </dt>
      <dd className="tabular-nums">{formatMoneyCents(regate.previous_outstanding_cents, currency)}</dd>
      <dt className="text-muted-foreground">
        {i18n._({ id: 'dunning.blocker.regate.newTotal', message: 'New total' })}
      </dt>
      <dd className="tabular-nums font-semibold">{formatMoneyCents(regate.outstanding_cents, currency)}</dd>
      {difference !== 0 && (
        <>
          <dt className="text-muted-foreground">
            {i18n._({ id: 'dunning.blocker.regate.difference', message: 'Difference' })}
          </dt>
          <dd className="tabular-nums">{formatMoneyCents(difference, currency)}</dd>
        </>
      )}
      {added.length > 0 && (
        <>
          <dt className="text-muted-foreground">
            {i18n._({ id: 'dunning.blocker.regate.added', message: 'Added invoices' })}
          </dt>
          <dd data-testid="dunning-regate-added">{added.join(', ')}</dd>
        </>
      )}
      {removed.length > 0 && (
        <>
          <dt className="text-muted-foreground">
            {i18n._({ id: 'dunning.blocker.regate.removed', message: 'Removed invoices' })}
          </dt>
          <dd data-testid="dunning-regate-removed">{removed.join(', ')}</dd>
        </>
      )}
    </dl>
  );
}
